import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import type {
  CampaignBreadcrumbData,
  OffPlatformBreadcrumbData,
} from './breadcrumbDataTypes';
import {createBreadcrumbRoutes} from './breadcrumbRouteTypes';
import {buildCampaignNameBreadcrumbItem} from './breadcrumbUtils';

function getMixedMediaNameFromNetwork(
  networkName: string | null | undefined
): string {
  if (!networkName) {
    return translate('Story');
  }
  if (networkName.includes('tiktok') || networkName.includes('pinterest')) {
    return translate('Carousel');
  }

  return translate('Story');
}

export const CAMPAIGN_CREATORS_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<CampaignBreadcrumbData>([
    {
      path: `${ROUTES.CAMPAIGNS_V2}/:campaignId/creators${ROUTES.CREATOR_PROFILE}/:creatorId`,
      getItems: (params, data) => [
        {label: translate('Campaigns'), link: ROUTES.CAMPAIGNS_V2},
        buildCampaignNameBreadcrumbItem(
          data.campaignName ?? '',
          data.networkName,
          `${ROUTES.CAMPAIGNS_V2}/${params.campaignId}/creators`
        ),
        {label: data.creatorName ?? ''},
      ],
    },
  ]);

export const CAMPAIGN_ASSETS_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<CampaignBreadcrumbData>([
    {
      path: `${ROUTES.CAMPAIGNS_V2}/:campaignId/assets/mixed-media/:parentContentSubmissionId/:contentSubmissionId`,
      getItems: (params, data) => [
        {label: translate('Campaigns'), link: ROUTES.CAMPAIGNS_V2},
        buildCampaignNameBreadcrumbItem(
          data.campaignName ?? '',
          data.networkName,
          `${ROUTES.CAMPAIGNS_V2}/${params.campaignId}/assets`
        ),
        {
          label: getMixedMediaNameFromNetwork(data.networkName),
          link: `${ROUTES.CAMPAIGNS_V2}/${params.campaignId}/assets/mixed-media/${params.parentContentSubmissionId}`,
        },
        {label: data.contentTypeLabel ?? ''},
      ],
    },
    {
      path: `${ROUTES.CAMPAIGNS_V2}/:campaignId/assets/:contentSubmissionId`,
      getItems: (params, data) => [
        {label: translate('Campaigns'), link: ROUTES.CAMPAIGNS_V2},
        buildCampaignNameBreadcrumbItem(
          data.campaignName ?? '',
          data.networkName,
          `${ROUTES.CAMPAIGNS_V2}/${params.campaignId}/assets`
        ),
        {label: data.contentTypeLabel ?? ''},
      ],
    },
    {
      path: `${ROUTES.CAMPAIGNS_V2}/:campaignId/assets/mixed-media/:parentContentSubmissionId`,
      getItems: (params, data) => [
        {label: translate('Campaigns'), link: ROUTES.CAMPAIGNS_V2},
        buildCampaignNameBreadcrumbItem(
          data.campaignName ?? '',
          data.networkName,
          `${ROUTES.CAMPAIGNS_V2}/${params.campaignId}/assets`
        ),
        {label: getMixedMediaNameFromNetwork(data.networkName)},
      ],
    },
  ]);

export const CAMPAIGN_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<CampaignBreadcrumbData>([
    {
      path: `${ROUTES.CAMPAIGNS_V2}/:campaignId/*`,
      getItems: (_, data) => [
        {label: translate('Campaigns'), link: ROUTES.CAMPAIGNS_V2},
        buildCampaignNameBreadcrumbItem(
          data.campaignName ?? '',
          data.networkName
        ),
      ],
    },
  ]);

export const OFF_PLATFORM_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<OffPlatformBreadcrumbData>([
    {
      path: `${ROUTES.OFF_PLATFORM_REVIEW}/:campaignId/mixed-media/:parentContentSubmissionId/:contentSubmissionId`,
      getItems: (params, data) => [
        {
          label: data.campaignName ?? '',
          link: `${ROUTES.OFF_PLATFORM_REVIEW}/${params.campaignId}`,
        },
        {
          label: getMixedMediaNameFromNetwork(data.networkName),
          link: `${ROUTES.OFF_PLATFORM_REVIEW}/${params.campaignId}/mixed-media/${params.parentContentSubmissionId}`,
        },
        {label: data.contentTypeLabel ?? ''},
      ],
    },
    {
      path: `${ROUTES.OFF_PLATFORM_REVIEW}/:campaignId/:contentSubmissionId`,
      getItems: (params, data) => [
        {
          label: data.campaignName ?? '',
          link: `${ROUTES.OFF_PLATFORM_REVIEW}/${params.campaignId}`,
        },
        {label: data.contentTypeLabel ?? ''},
      ],
    },
    {
      path: `${ROUTES.OFF_PLATFORM_REVIEW}/:campaignId/mixed-media/:parentContentSubmissionId`,
      getItems: (params, data) => [
        {
          label: data.campaignName ?? '',
          link: `${ROUTES.OFF_PLATFORM_REVIEW}/${params.campaignId}`,
        },
        {label: getMixedMediaNameFromNetwork(data.networkName)},
      ],
    },
  ]);

type CreatorBreadcrumbData = {
  creatorName?: string;
};

export const CREATOR_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<CreatorBreadcrumbData>([
    {
      path: `${ROUTES.CREATOR_CRM}${ROUTES.CREATOR_PROFILE}/:creatorId`,
      getItems: (_, data) => [
        {label: translate('Creators'), link: ROUTES.CREATOR_CRM},
        {label: data.creatorName ?? ''},
      ],
    },
    {
      path: `${ROUTES.CREATOR_CRM}/off-platform/:offPlatformCreatorId`,
      getItems: (_, data) => [
        {label: translate('Creators'), link: ROUTES.CREATOR_CRM},
        {label: data.creatorName ?? ''},
      ],
    },
  ]);

type ListsBreadcrumbData = {
  creatorName?: string;
};

export const LISTS_BREADCRUMB_ROUTES =
  createBreadcrumbRoutes<ListsBreadcrumbData>([
    {
      path: `${ROUTES.MY_CREATORS}${ROUTES.CREATOR_PROFILE}/:creatorId`,
      getItems: (_, data) => [
        {label: translate('Lists'), link: ROUTES.MY_CREATORS},
        {label: data.creatorName ?? ''},
      ],
    },
  ]);
